import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { conversationsApi, sendApi } from "@/api";
import type { Contact, Message } from "@/types";
import { countSmsChars, SMS_PREFIX } from "@/utils/sms";

function ChatBubble({ msg }: { msg: Message }) {
  const isOutbound = msg.direction === "OUTBOUND";
  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"} mb-2`}>
      <div
        className={`max-w-xs px-3 py-2 rounded-2xl text-sm ${
          isOutbound
            ? "bg-blue-500 text-white rounded-br-sm"
            : "bg-slate-100 text-slate-900 rounded-bl-sm"
        }`}
      >
        <p>{msg.body}</p>
        <p
          className={`text-[10px] mt-1 ${
            isOutbound ? "text-blue-100" : "text-slate-400"
          }`}
        >
          {new Date(msg.createdAt).toLocaleTimeString("ja-JP", {
            hour: "2-digit",
            minute: "2-digit",
          })}
          {isOutbound && msg.status === "DELIVERED" && " ✓✓"}
          {isOutbound && msg.status === "FAILED" && " ✕"}
        </p>
      </div>
    </div>
  );
}

export default function Conversations() {
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const queryClient = useQueryClient();

  const { data: contacts } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => conversationsApi.list(),
  });

  const { data: messages } = useQuery({
    queryKey: ["conversation-messages", selectedContact?.id],
    queryFn: () => conversationsApi.messages(selectedContact!.id),
    enabled: !!selectedContact,
    refetchInterval: 10000,
  });

  const [showCostConfirm, setShowCostConfirm] = useState(false);

  const sendMutation = useMutation({
    mutationFn: (body: string) =>
      sendApi.send({ to: selectedContact!.phone, body }),
    onSuccess: () => {
      setNewMessage("");
      setShowCostConfirm(false);
      queryClient.invalidateQueries({
        queryKey: ["conversation-messages", selectedContact?.id],
      });
    },
  });

  const msgInfo = newMessage ? countSmsChars(newMessage) : null;

  const handleSend = () => {
    if (!newMessage.trim()) return;
    if (msgInfo && msgInfo.segments >= 2 && !showCostConfirm) {
      setShowCostConfirm(true);
      return;
    }
    sendMutation.mutate(newMessage);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">会話</h2>

      <div className="flex gap-4 h-[calc(100vh-180px)]">
        {/* 連絡先リスト */}
        <div className="w-72 bg-white rounded-lg shadow-sm border overflow-auto">
          {contacts?.data.map((contact) => (
            <button
              key={contact.id}
              onClick={() => setSelectedContact(contact)}
              className={`w-full text-left px-4 py-3 border-b text-sm hover:bg-slate-50 ${
                selectedContact?.id === contact.id ? "bg-blue-50" : ""
              }`}
            >
              <p className="font-medium">{contact.name}</p>
              <p className="text-xs text-slate-500 font-mono">
                {contact.phone}
              </p>
              {contact.lastMessage && (
                <p className="text-xs text-slate-400 truncate mt-1">
                  {contact.lastMessage.body}
                </p>
              )}
            </button>
          ))}
          {!contacts?.data.length && (
            <p className="p-4 text-sm text-slate-400">連絡先がありません</p>
          )}
        </div>

        {/* チャットエリア */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border flex flex-col">
          {selectedContact ? (
            <>
              <div className="px-4 py-3 border-b">
                <p className="font-medium">{selectedContact.name}</p>
                <p className="text-xs text-slate-500">{selectedContact.phone}</p>
              </div>
              <div className="flex-1 overflow-auto p-4">
                {messages?.data.map((msg) => (
                  <ChatBubble key={msg.id} msg={msg} />
                ))}
              </div>
              <div className="border-t">
                {/* コスト確認ダイアログ */}
                {showCostConfirm && msgInfo && (
                  <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-sm">
                    <p className="font-medium text-amber-800">
                      この送信は {msgInfo.segments}通分（{(msgInfo.segments * 3.3).toFixed(1)}円）です
                    </p>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => sendMutation.mutate(newMessage)}
                        disabled={sendMutation.isPending}
                        className="px-3 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700"
                      >
                        このまま送信
                      </button>
                      <button
                        onClick={() => setShowCostConfirm(false)}
                        className="px-3 py-1 border border-amber-300 rounded text-xs text-amber-700"
                      >
                        本文を修正
                      </button>
                    </div>
                  </div>
                )}
                {/* 文字数・コスト表示 */}
                {newMessage.length > 0 && msgInfo && (
                  <div className={`px-3 py-1 flex justify-between text-xs ${
                    msgInfo.overLimit ? "bg-red-50 text-red-600" : msgInfo.segments >= 2 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-500"
                  }`}>
                    <span>
                      {SMS_PREFIX.length}+{newMessage.length}={msgInfo.estimatedChars}文字
                      {msgInfo.segments > 0 && ` / ${msgInfo.segments}通（${(msgInfo.segments * 3.3).toFixed(1)}円）`}
                    </span>
                    <span>
                      {msgInfo.segments >= 2 ? "70文字超で複数通課金" : `あと${70 - msgInfo.estimatedChars}文字で2通`}
                    </span>
                  </div>
                )}
                <div className="p-3 flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      setShowCostConfirm(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newMessage.trim()) {
                        handleSend();
                      }
                    }}
                    placeholder="メッセージを入力..."
                    className="flex-1 px-3 py-2 border rounded-md text-sm"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sendMutation.isPending || msgInfo?.overLimit}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
                  >
                    送信
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              左から連絡先を選択してください
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
